<template>
    <div>
        <!-- Materials. -->
        <div class="flex border -mb-px border-gray-200 bg-blue-50" :class="compactMode ? '' : 'rounded-t-lg'">
            <div class="flex-1 text-xs font-semibold px-2 py-2.5">Materials</div>
            <div class="w-40 -ml-px text-xs font-semibold px-2 py-2.5 text-left">Supplier</div>
            <div class="w-32 -ml-px text-xs font-semibold px-2 py-2.5 text-right">Price</div>
            <div class="w-32 -ml-px text-xs font-semibold px-2 py-2.5 text-right">Coverage</div>
            <div class="w-32 -ml-px text-xs font-semibold px-2 py-2.5 text-right">Price per {{ form.unit_of_measure }}</div>
            <div class="w-32 -ml-px text-xs font-semibold px-2 py-2.5 text-right" v-if="showTotal">Total</div>
            <div class="w-8"></div>
        </div>
        <global-specs-form-material-row
            v-for="material in form.materials"
            :key="material.id"
            :form="form"
            :estimate="estimate"
            :item="material"
            :quantity="quantity"
            :read-only="readOnly"
            @updated="updatedMaterial"
        ></global-specs-form-material-row>
        <div class="flex border-t border-b -mb-px border-gray-200 bg-white" v-if="! readOnly">
            <div class="flex-1 relative">
                <materials-search-dropdown #default="{ open, query, setQuery }" :uom="form.unit_of_measure" @selected="addMaterial" :estimate="estimate">
                    <input
                        class="text-sm -my-px pl-8 pr-2 py-1.5 border border-gray-200 shadow-none relative"
                        type="text"
                        placeholder="Add a new material..."
                        :value="query"
                        @input="setQuery($event.target.value)"
                        @focus="open"
                    >
                </materials-search-dropdown>
                <div class="absolute inset-y-0 left-0 w-8 flex items-center justify-center text-sm text-gray-400">
                    <i class="fa fa-plus"></i>
                </div>
            </div>
        </div>
        <div class="flex border -mb-px px-2 py-1.5 border-gray-200 bg-white text-sm text-gray-500 italic" v-else-if="form.materials.length === 0">
            No materials
        </div>

        <!-- Labour Rates. -->
        <div class="flex border -mb-px border-gray-200 bg-blue-50">
            <div class="flex-1 text-xs font-semibold px-2 py-2.5">Labour Rates</div>
            <div class="w-32 -ml-px text-xs font-semibold px-2 py-2.5 text-right">Rate per hour</div>
            <div class="w-32 -ml-px text-xs font-semibold px-2 py-2.5 text-right">Output per hour</div>
            <div class="w-32 -ml-px text-xs font-semibold px-2 py-2.5 text-right">Price per {{ form.unit_of_measure }}</div>
            <div class="w-32 -ml-px text-xs font-semibold px-2 py-2.5 text-right" v-if="showTotal">Total</div>
            <div class="w-8"></div>
        </div>
        <global-specs-form-labour-rate-row
            v-for="labourRate in form.labour"
            :key="labourRate.id"
            :form="form"
            :estimate="estimate"
            :item="labourRate"
            :labour-rates="labourRates"
            :labour-rate-groups="labourRateGroups"
            :quantity="quantity"
            :read-only="readOnly"
            @updated="updatedLabourRate"
            @new:labour="refreshLabourRateOptions"
        ></global-specs-form-labour-rate-row>
        <div class="flex border-t border-b -mb-px border-gray-200 bg-white" v-if="! readOnly">
            <div class="flex-1 relative">
                <labour-rates-search-dropdown
                    #default="{ open, query, setQuery }"
                    :uom="form.unit_of_measure"
                    :labour-rates="labourRates"
                    :labour-rate-groups="labourRateGroups"
                    @selected="addLabourRate"
                    @created="refreshLabourRateOptions"
                >
                    <input
                        class="text-sm -my-px pl-8 pr-2 py-1.5 border border-gray-200 shadow-none relative"
                        type="text"
                        placeholder="Add a new labour rate..."
                        :value="query"
                        @input="setQuery($event.target.value)"
                        @focus="open"
                    >
                </labour-rates-search-dropdown>
                <div class="absolute inset-y-0 left-0 w-8 flex items-center justify-center text-sm text-gray-400">
                    <i class="fa fa-plus"></i>
                </div>
            </div>
        </div>
        <div class="flex border -mb-px px-2 py-1.5 border-gray-200 bg-white text-sm text-gray-500 italic" v-else-if="form.labour.length === 0">
            No labour rates
        </div>

        <!-- Total lines. -->
        <div class="border -mb-px border-gray-200 bg-blue-50 rounded-b-lg">
            <div class="flex text-right">
                <div class="flex-1 text-sm px-2 py-1">Materials:</div>
                <div class="w-32 -ml-px text-sm px-2 py-1">{{ totalMaterialRate | toCurrency }}</div>
                <div class="w-32 -ml-px text-sm px-2 py-1" v-if="showTotal">{{ totalMaterialRate * quantity | toCurrency }}</div>
                <div class="w-8"></div>
            </div>
            <div class="flex text-right">
                <div class="flex-1 text-sm px-2 py-1">Labour rates:</div>
                <div class="w-32 -ml-px text-sm px-2 py-1">{{ totalLabourRate | toCurrency }}</div>
                <div class="w-32 -ml-px text-sm px-2 py-1" v-if="showTotal">{{ totalLabourRate * quantity | toCurrency }}</div>
                <div class="w-8"></div>
            </div>
            <div class="flex text-right">
                <div class="flex-1 text-sm font-semibold px-2 py-1">Total:</div>
                <div class="w-32 -ml-px text-sm font-semibold px-2 py-1">{{ totalRate | toCurrency }}</div>
                <div class="w-32 -ml-px text-sm font-semibold px-2 py-1" v-if="showTotal">{{ totalRate * quantity | toCurrency }}</div>
                <div class="w-8"></div>
            </div>
        </div>
    </div>
</template>

<script>
import { LabourRate, GlobalSpec } from '@models'

export default {
    props: {
        form: Object,
        estimate: Object,
        quantity: Number,
        compactMode: Boolean,
        readOnly: Boolean,
    },
    data () {
        return {
            labourRates: [],
            labourRateGroups: [],
        }
    },
    computed: {
        showTotal () {
            return this.quantity !== undefined
        },
        totalMaterialRate () {
            return this.form.materials_cost_per_uom ?? 0
        },
        totalLabourRate () {
            return this.form.labour_cost_per_uom ?? 0
        },
        totalRate () {
            return this.form.cost_per_uom ?? 0
        },
    },
    methods: {
        async fetchLabourRates () {
            this.labourRates = await LabourRate.all()
        },
        async fetchLabourRateGroups () {
            this.labourRateGroups = await LabourRate.groups()
        },
        refreshLabourRateOptions () {
            this.fetchLabourRates()
            this.fetchLabourRateGroups()
        },
        async addMaterial (material) {
            const newMaterialLine = {
                id: null,
                name: material.name,
                unit_of_measure: '',
                coverage: 0,
                price_per_item: 0,
                price_per_uom: 0,
                material: material,
                new_line: true,
                company: null,
                company_id: null,
                attached_companies: material.attached_companies,
                current_company: material.current_company,
                default_company: material.default_company,
                item_usages: material.item_usages ?? [],
                spec_usages: material.spec_usages ?? [],
            }

            const newSupplier = material.current_company ?? material.default_company ?? null
            newMaterialLine.price_per_item = newSupplier?.cost ?? 0

            if (this.estimate && newSupplier) {
                newMaterialLine.company_id = newSupplier.company_id
                newMaterialLine.company = {
                    id: newSupplier.company_id,
                    name: newSupplier.company_name,
                }
            }

            this.form.materials.push(newMaterialLine);
            this.updatedMaterial()
        },
        async addLabourRate (labourRate) {
            const newLabourRateLine = {
                id: null,
                name: labourRate.full_name,
                rate: labourRate,
                coverage_per_hour: 0,
                price_per_hour: 0,
                hours_per_day: 0,
                price_per_day: 0,
                price_per_uom: 0,
                new_line: true
            }

            this.form.labour.push(newLabourRateLine);
            const prices = await LabourRate.price(labourRate.id, this.estimate?.id)
            newLabourRateLine.price_per_day = prices.price_per_day
            newLabourRateLine.hours_per_day = prices.hours_per_day
            newLabourRateLine.price_per_hour = prices.price_per_hour
            newLabourRateLine.coverage_per_hour = prices.coverage_per_hour
            newLabourRateLine.price_per_uom = prices.price_per_uom
            this.updated()
        },
        updated (payload = {}) {
            GlobalSpec.recalculateTotals(this.form)
            this.$emit('updated', payload)
        },
        updatedLabourRate(payload = {}){
          this.form.labour = this.form.labour.map((lr)=>{
            lr.price_per_day = (lr.price_per_hour * lr.hours_per_day)

            return lr
          })

          this.updated()
        },
        updatedMaterial () {
            this.updated({ is_material_change: true })
        },
    },
    mounted () {
        this.refreshLabourRateOptions()
    },
}
</script>
